"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useEffect, useState } from "react";

interface MakePrimaryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (moveBillingToNewPrimary: boolean) => void;
  userName: string;
  /** Who Stripe bills today, for the "keep billing" option. */
  currentBillingName?: string;
  /** True when the incoming primary already is the billing member. */
  isNewPrimaryAlreadyBilling?: boolean;
  isLoading?: boolean;
}

export function MakePrimaryDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  userName,
  currentBillingName,
  isNewPrimaryAlreadyBilling = false,
  isLoading = false,
}: MakePrimaryDialogProps) {
  const [moveBilling, setMoveBilling] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMoveBilling(false);
    }
  }, [isOpen]);

  // With nothing to move, the choice would be a no-op either way.
  const showBillingChoice = !isNewPrimaryAlreadyBilling && !!currentBillingName;

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Make Primary User</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-muted-foreground text-sm">
              <p>
                Are you sure you want to make <strong>{userName}</strong> the
                primary user?
              </p>
              <p>
                This action will transfer primary account ownership to{" "}
                <strong>{userName}</strong>. Your account will become a
                secondary account, and you will no longer be able to manage
                users or perform primary account actions.
              </p>
              <p className="font-semibold text-destructive">
                This action can only be undone by the new primary user.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {showBillingChoice ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Stripe billing account</h3>
              <p className="text-sm text-muted-foreground">
                The billing account is the organization member whose Stripe
                customer is charged for card and ACH payments, and whose saved
                payment methods appear in the billing portal. Changing primary
                does not have to change it.
              </p>
            </div>
            <RadioGroup
              value={moveBilling ? "move" : "keep"}
              onValueChange={(value) => setMoveBilling(value === "move")}
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem
                  value="keep"
                  id="make-primary-billing-keep"
                  className="mt-1"
                />
                <Label
                  htmlFor="make-primary-billing-keep"
                  className="font-normal"
                >
                  Keep billing with <strong>{currentBillingName}</strong> —
                  payments keep using their Stripe customer and saved payment
                  methods, even though they will now be a secondary user.
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem
                  value="move"
                  id="make-primary-billing-move"
                  className="mt-1"
                />
                <Label
                  htmlFor="make-primary-billing-move"
                  className="font-normal"
                >
                  Bill <strong>{userName}</strong> instead — future payments use
                  their Stripe customer.{" "}
                  <span className="text-muted-foreground">
                    Saved cards and bank accounts do not move; anything saved by{" "}
                    {currentBillingName} stays on their Stripe customer and can
                    be restored by switching billing back.
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(moveBilling)}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/80 text-destructive-foreground"
          >
            {isLoading ? "Transferring..." : "Make Primary"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
